import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/context/LanguageContext";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import CookieBanner from "./components/CookieBanner";
import TrackingProvider from "./components/TrackingProvider";
import PasswordRecoveryRedirect from "./components/admin/PasswordRecoveryRedirect";
import PerfProbe from "./components/PerfProbe"; // ⚠️ TEMPORAL (diagnóstico ?perf=1)
import { LandingAppsPage } from "./landing";
import HomeFirstPaint from "./landing/inicio/HomeFirstPaint";
import KeepQueryRedirect from "./components/KeepQueryRedirect";
import AppErrorBoundary from "./components/AppErrorBoundary";

// Lazy-loaded pages
// Home nueva: chunk propio (no engorda el bundle del resto de páginas). En "/" se precarga desde
// el HTML (modulepreload que añade scripts/seo-prerender.ts) y main.tsx lo pide al arrancar.
const HomeInicio = React.lazy(() => import("./landing/inicio/HomeInicio"));
// Páginas nuevas de Claude Design (src/ulture/): /tactic, /pest, /sobre-nosotros, /partners, /blog y /contacto.
// El envoltorio (cabecera, pie, idioma, meta) y cada página son chunks propios.
const UltureSitePage = React.lazy(() => import("./ulture/UltureSite"));
const BlogPost = React.lazy(() => import("./pages/BlogPost"));
const Booking = React.lazy(() => import("./pages/Booking"));
const CaseStudies = React.lazy(() => import("./pages/CaseStudies"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Terms = React.lazy(() => import("./pages/Terms"));
const CookiePolicy = React.lazy(() => import("./pages/CookiePolicy"));
const Dpa = React.lazy(() => import("./pages/Dpa"));
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));
const Admin = React.lazy(() => import("./pages/Admin"));
const AdminTranslations = React.lazy(() => import("./pages/AdminTranslations"));
const AdminAnalytics = React.lazy(() => import("./pages/AdminAnalytics"));
const AdminResetPassword = React.lazy(() => import("./pages/AdminResetPassword"));
const AdminBlog = React.lazy(() => import("./pages/AdminBlog"));
const AdminLegal = React.lazy(() => import("./pages/AdminLegal"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const AgriculturalManagementSoftware = React.lazy(() => import("./pages/AgriculturalManagementSoftware"));
const FarmOperationsSoftware = React.lazy(() => import("./pages/FarmOperationsSoftware"));
const GreenhouseManagementSoftware = React.lazy(() => import("./pages/GreenhouseManagementSoftware"));
const PestManagementSoftware = React.lazy(() => import("./pages/PestManagementSoftware"));
const AgricultureErp = React.lazy(() => import("./pages/AgricultureErp"));
const SoftwareAgricola = React.lazy(() => import("./pages/SoftwareAgricola"));
const CuadernoCampoDigital = React.lazy(() => import("./pages/CuadernoCampoDigital"));
const Prensa = React.lazy(() => import("./pages/Prensa"));
const Opiniones = React.lazy(() => import("./pages/Opiniones"));
const Unsubscribe = React.lazy(() => import("./pages/Unsubscribe"));
const BookingManage = React.lazy(() => import("./pages/BookingManage"));

const queryClient = new QueryClient();

const pageTransitionVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

// La primera página que se carga entra sin fundido: el HTML ya pinta su primer fotograma
// (en "/" la tarjeta del hero) y un fundido desde opacidad 0 dejaba ver el fondo un instante.
let firstRoute = true;

function AnimatedRoutes() {
  const location = useLocation();
  const skipFade = firstRoute;
  React.useEffect(() => { firstRoute = false; }, []);

  return (
    <motion.div
      key={location.pathname}
      variants={pageTransitionVariants}
      initial={skipFade ? false : "initial"}
      animate="animate"
      transition={{ duration: 0.1, ease: "easeOut" }}
    >
        <Suspense fallback={null}>
          <Routes location={location}>
            {/* Landing nueva (Ulture.co Web) */}
            <Route path="/" element={<Suspense fallback={<HomeFirstPaint />}><HomeInicio /></Suspense>} />
            {/* ulture.co/tactic y ulture.co/pest → la página de cada app (diseño nuevo, no saltan a sus webs) */}
            <Route path="/tactic" element={<UltureSitePage page="tactic" />} />
            <Route path="/pest" element={<UltureSitePage page="pest" />} />
            <Route path="/sobre-nosotros" element={<UltureSitePage page="sobre-nosotros" />} />
            <Route path="/partners" element={<UltureSitePage page="partners" />} />
            <Route path="/contacto" element={<UltureSitePage page="contacto" />} />
            <Route path="/apps" element={<LandingAppsPage />} />
            {/* rutas antiguas → nuevas */}
            <Route path="/about" element={<KeepQueryRedirect to="/sobre-nosotros" />} />
            <Route path="/contact" element={<KeepQueryRedirect to="/contacto" />} />
            <Route path="/blog" element={<UltureSitePage page="blog" />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/personalize/case-studies" element={<CaseStudies />} />
            {/* rutas viejas → nuevas (en el build también hay páginas estáticas de redirección: scripts/seo-prerender.ts REDIRECTS) */}
            <Route path="/case-studies" element={<KeepQueryRedirect to="/personalize/case-studies" />} />
            <Route path="/personalize" element={<KeepQueryRedirect to="/" />} />
            <Route path="/software" element={<KeepQueryRedirect to="/" />} />
            <Route path="/ulture-tactic" element={<KeepQueryRedirect to="/tactic" />} />
            <Route path="/ulture-pest" element={<KeepQueryRedirect to="/pest" />} />
            <Route path="/booking" element={<Booking />} />
            
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/admin/dashboard" element={<Admin />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/translations" element={<AdminTranslations />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/legal" element={<AdminLegal />} />
            
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/agricultural-management-software" element={<AgriculturalManagementSoftware />} />
            <Route path="/farm-operations-software" element={<FarmOperationsSoftware />} />
            <Route path="/greenhouse-management-software" element={<GreenhouseManagementSoftware />} />
            <Route path="/pest-management-software" element={<PestManagementSoftware />} />
            <Route path="/agriculture-erp" element={<AgricultureErp />} />
            <Route path="/software-agricola" element={<SoftwareAgricola />} />
            <Route path="/cuaderno-de-campo-digital" element={<CuadernoCampoDigital />} />
            <Route path="/opiniones" element={<Opiniones />} />
            <Route path="/prensa" element={<Prensa />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/booking/manage" element={<BookingManage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <LanguageProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <TrackingProvider />
            <PasswordRecoveryRedirect />
            <AppErrorBoundary>
              <AnimatedRoutes />
            </AppErrorBoundary>
            <CookieBanner />
            {/* ⚠️ TEMPORAL: medidor de fps/jank, solo con ?perf=1 */}
            <PerfProbe />
          </BrowserRouter>
        </LanguageProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
